import React from "react";
import { Button } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "../../config/store";
import {
    importAllData,
    validateAllFiles,
    selectImportState,
    selectIsValidating
} from "../dataImportWizardSlice";
import { useTranslation } from "react-i18next";

type Step = 1 | 2 | 3 | 4;

type Props = {
    activeStep: Step;
    goTo: (s: Step) => void;
    canGoStep2: boolean;
    canGoStep3: boolean;
    canGoStep4: boolean;
    onFinish: () => void;
};

export default function ImportWizardFooter({
    activeStep,
    goTo,
    canGoStep2,
    canGoStep3,
    canGoStep4,
    onFinish
}: Props) {
    const { t } = useTranslation();
    const tt = (key: string) => t(key, { ns: "translation" });
    const dispatch = useAppDispatch();
    const importState = useAppSelector(selectImportState);
    const isValidating = useAppSelector(selectIsValidating);

    const showBack = activeStep > 1;
    const showNext = activeStep < 4;

    const isImporting = importState.status === "running";

    const nextDisabled =
        (activeStep === 1 && !canGoStep2) ||
        (activeStep === 2 && (!canGoStep3 || isValidating)) ||
        (activeStep === 3 && (!canGoStep4 || isImporting));

    const backDisabled =
        (activeStep === 2 && isValidating) ||
        (activeStep === 3 && isImporting);

    const back = () => goTo((activeStep - 1) as Step);
    const next = () => goTo((activeStep + 1) as Step);

    return (
        <div className="d-flex justify-content-between align-items-center gap-2">
            <div>
                {showBack && (
                    <Button variant="secondary" onClick={back} disabled={backDisabled}>
                        {tt("pages.thermography.dataImportWizard.footer.back")}
                    </Button>
                )}
            </div>

            <div className="d-flex gap-2">
                {/* STEP 2: botão VALIDAR ao lado do avançar */}
                {activeStep === 2 && (
                    <Button
                        variant="outline-primary"
                        onClick={() => dispatch(validateAllFiles())}
                        disabled={isValidating}
                    >
                        {isValidating ? tt("pages.thermography.dataImportWizard.footer.validating") : tt("pages.thermography.dataImportWizard.footer.validate")}
                    </Button>
                )}

                {/* STEP 3: botão IMPORTAR ao lado do avançar */}
                {activeStep === 3 && (
                    <Button
                        variant="outline-primary"
                        onClick={() => dispatch(importAllData())}
                        disabled={isImporting}
                    >
                        {isImporting ? tt("pages.thermography.dataImportWizard.footer.importing") : tt("pages.thermography.dataImportWizard.footer.importData")}
                    </Button>
                )}

                {/* STEP 4: CONCLUIR no lugar do próximo */}
                {activeStep === 4 ? (
                    <Button variant="primary" onClick={onFinish}>
                        {tt("pages.thermography.dataImportWizard.footer.finish")}
                    </Button>
                ) : (
                    showNext && (
                        <Button variant="primary" onClick={next} disabled={nextDisabled}>
                            {tt("pages.thermography.dataImportWizard.footer.next")}
                        </Button>
                    )
                )}
            </div>
        </div>
    );
}
